package scenes

import AppIntegrations
import AppState
import com.soywiz.korge.input.*
import com.soywiz.korge.scene.*
import com.soywiz.korge.ui.*
import com.soywiz.korge.view.*
import com.soywiz.korim.bitmap.*
import com.soywiz.korim.color.*
import com.soywiz.korim.font.*
import com.soywiz.korim.format.*
import com.soywiz.korim.text.*
import com.soywiz.korio.file.std.*
import com.soywiz.korma.geom.*
import i18n.*
import scenes.levels.*
import ui.*
import utils.*

class LobbyScene : Scene() {

    private var titleFont: Font? = null

    private lateinit var appState: AppState

    private val isDesktop = AppIntegrations.yaSdk?.deviceInfo?.isDesktop() ?: true

    private val modalDialogController = ModalDialogController()

    private var selectedLevel = 0
    private val levelButtons = mutableListOf<CustomButton>()

    private var buttonIconGreen: Bitmap? = null
    private var buttonIconPurple: Bitmap? = null
    private var buttonIconActive: Bitmap? = null
    private var levelButtonBackground: Bitmap? = null
    private var levelButtonFrame: Bitmap? = null
    private var levelButtonFrameActive: Bitmap? = null

    private val levelMap = mapOf(
        1 to Level1Scene::class,
        2 to Level2Scene::class,
        3 to Level3Scene::class,
        4 to Level4Scene::class,
        5 to Level5Scene::class,
        6 to Level6Scene::class
    )

    override suspend fun SContainer.sceneInit() {
        appState = injector.get<AppState>()

        titleFont = FontsLoader.loadFont(FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC)

        val bg = resourcesVfs["background/lobby.png"].readBitmap()
        val bgImage = image(bg)
        UIView.fitIconInRect(
            bgImage,
            bg.slice(),
            GameDimensions.V_WIDTH.toDouble(),
            GameDimensions.V_HEIGHT.toDouble(),
            Anchor.CENTER
        )

        buttonIconGreen = resourcesVfs["lobby_buttons/lobby_green.png"].readBitmap()
        buttonIconPurple = resourcesVfs["lobby_buttons/lobby_purple.png"].readBitmap()
        buttonIconActive = resourcesVfs["lobby_buttons/lobby_pressed.png"].readBitmap()
        levelButtonBackground = resourcesVfs["lobby_buttons/forest_button.png"].readBitmap()
        levelButtonFrame = resourcesVfs["lobby_buttons/frame.png"].readBitmap()
        levelButtonFrameActive = resourcesVfs["lobby_buttons/frame_active.png"].readBitmap()
    }

    override suspend fun SContainer.sceneMain() {

        level1Button()
            .apply {
                setActive()
                registerOnModalController("lb_1", modalDialogController)
                levelButtons.add(this)
            }
        level2Button()
            .apply {
                registerOnModalController("lb_2", modalDialogController)
                levelButtons.add(this)
            }
        level3Button()
            .apply {
                registerOnModalController("lb_3", modalDialogController)
                levelButtons.add(this)
            }
        level4Button()
            .apply {
                registerOnModalController("lb_4", modalDialogController)
                levelButtons.add(this)
            }
        level5Button()
            .apply {
                registerOnModalController("lb_5", modalDialogController)
                levelButtons.add(this)
            }
        level6Button()
            .apply {
                registerOnModalController("lb_6", modalDialogController)
                levelButtons.add(this)
            }

        val selectedLevelText = selectedLevelText()

        levelButtons.forEachIndexed { i, button ->
            button.onPress {
                levelButtons[selectedLevel].apply {
                    forcedPressed = false
                    setIdle()
                }
                selectedLevel = i
                button.apply {
                    forcedPressed = true
                    setActive()
                }
                selectedLevelText.text = getSelectedLeveText()
            }
        }

        playButton().apply {
            registerOnModalController("play", modalDialogController)
        }
        controlsButton().apply {
            registerOnModalController("controls", modalDialogController)
        }
    }

    private fun CustomButton.registerOnModalController(id: String, controller: ModalDialogController) {
        controller.registerOnShow(id) {
            this.disable()
        }

        controller.registerOnHide(id) {
            this.enable()
        }
    }

    private fun Container.keysInfoTitle(): UIText {
        val marginRight = 64.0
        val marginTop = 64.0
        val width = 258.6
        val height = 82.5
        val posX = GameDimensions.V_WIDTH / 2.0 - width / 2.0
        val posY = marginTop

        return uiText(
            text = Localization.strings.scene.lobby.controlsTitle,
            width = width,
            height = height
        )
            .position(posX, posY)
            .apply {
                textSize = 32.0
                textAlignment = TextAlignment.MIDDLE_CENTER
                textFont = titleFont ?: DefaultUIFont
            }
    }

    private fun Container.keysInfoText(): UIText {
        val marginRight = 64.0
        val marginTop = 64.0
        val width = 258.6
        val height = 301.5
        val posX = GameDimensions.V_WIDTH / 2.0 - width / 2.0
        val posY = marginTop

        val text = getKeysInfoText()

        return if (isDesktop) {
            uiText(
                text = text,
                width = width,
                height = height
            )
                .position(posX, posY)
                .apply {
                    textSize = 28.0
                    textAlignment = TextAlignment.MIDDLE_LEFT
                    textFont = titleFont ?: DefaultUIFont
                }
        } else {
            uiText(
                text = text,
                width = GameDimensions.V_WIDTH.toDouble(),
                height = height
            )
                .position(0.0, posY)
                .apply {
                    textSize = 28.0
                    textAlignment = TextAlignment.MIDDLE_CENTER
                    textFont = titleFont ?: DefaultUIFont
                }
        }
    }

    private fun getKeysInfoText(): String {
        return if (isDesktop) {
            """
                ${Localization.strings.scene.lobby.controlLeftDesktop}
                ${Localization.strings.scene.lobby.controlRightDesktop}
                ${Localization.strings.scene.lobby.controlJumpDesktop}
                ${Localization.strings.scene.lobby.controlDashDesktop}
            """.trimIndent()
        } else {
            """
                ${Localization.strings.scene.lobby.controlsTouch}
            """.trimIndent()
        }
    }

    private fun Container.selectedLevelText(): UIText {
        val width = 451.0
        val height = 82.5
        val marginLeft = 64.0
        val marginBottom = 64.0
        val posX = marginLeft
        val posY = GameDimensions.V_HEIGHT - height - marginBottom

        return uiText(
            text = getSelectedLeveText(),
            width = width,
            height = height
        )
            .position(posX, posY)
            .apply {
                textSize = 32.0
                textAlignment = TextAlignment.BOTTOM_LEFT
                textFont = titleFont ?: DefaultUIFont
            }
    }

    private fun getSelectedLeveText(): String {
        return "${Localization.strings.scene.lobby.selectedLevel}:  ${selectedLevel + 1}"
    }

    private fun Container.level1Button() = levelButton(
        label = "1",
        marginLeft = 64.0,
        marginTop = 64.0
    )

    private fun Container.level2Button() = levelButton(
        label = "2",
        posX = 64.0 + 128.0,
        marginLeft = 33.5,
        marginTop = 64.0
    )

    private fun Container.level3Button() = levelButton(
        label = "3",
        posX = 64.0 + 128.0 + 33.5 + 128.0,
        marginLeft = 33.5,
        marginTop = 64.0
    )

    private fun Container.level4Button() = levelButton(
        label = "4",
        posY = 64.0 + 128.0,
        marginLeft = 64.0,
        marginTop = 33.5
    )

    private fun Container.level5Button() = levelButton(
        label = "5",
        posX = 64.0 + 128.0,
        posY = 64.0 + 128.0,
        marginLeft = 33.5,
        marginTop = 33.5
    )

    private fun Container.level6Button() = levelButton(
        label = "6",
        posX = 64.0 + 128.0 + 33.5 + 128.0,
        posY = 64.0 + 128.0,
        marginLeft = 33.5,
        marginTop = 33.5
    )

    private fun Container.levelButton(
        label: String,
        posX: Double = 0.0,
        posY: Double = 0.0,
        marginLeft: Double = 0.0,
        marginRight: Double = 0.0,
        marginTop: Double = 0.0,
        marginBottom: Double = 0.0
    ): CustomButton {
        val width = 128.0
        val height = 128.0

        val button = CustomButton(
            label = label,
            width = width,
            height = height,
            bgIcon = levelButtonBackground?.slice(),
            icon = levelButtonFrame?.slice(),
            activeIcon = levelButtonFrameActive?.slice(),
            buttonId = -1
        )
        button.container.addTo(this)
        button.container.position(
            posX + marginLeft,
            posY + marginTop
        )
        button.text.apply {
            textSize = 48.0
            textFont = titleFont ?: DefaultUIFont
        }
        return button
    }

    private fun Container.playButton(): CustomButton {
        val width = 258.6
        val height = 82.5
        val marginRight = 64.0
        val marginTop = 64.0

        val button = CustomButton(
            label = Localization.strings.scene.lobby.playButton,
            width = width,
            height = height,
            icon = buttonIconGreen?.slice(),
            activeIcon = buttonIconActive?.slice(),
            buttonId = -1
        )

        button.container.addTo(this)
        button.container.position(
            GameDimensions.V_WIDTH - width - marginRight,
            marginTop
        )
        button.text.apply {
            textSize = 36.0
            textFont = titleFont ?: DefaultUIFont
        }

        button.onUp {
            val levelNum = selectedLevel + 1
            val level = levelMap[levelNum]
            if (level != null) {
                appState.sceneToLoad = level
                appState.nextLevel = levelNum
                sceneContainer.changeToAsync<SceneLoadingScene>()
            }
        }

        return button
    }

    private fun Container.controlsButton(): CustomButton {
        val baseView = this

        val width = 258.6
        val height = 82.5
        val marginRight = 64.0
        val marginTop = 64.0
        val buttonsMargin = 27.0

        val button = CustomButton(
            label = Localization.strings.scene.lobby.controlsTitle,
            width = width,
            height = height,
            icon = buttonIconPurple?.slice(),
            activeIcon = buttonIconActive?.slice(),
            buttonId = -1
        )

        button.container.addTo(this)
        button.container.position(
            GameDimensions.V_WIDTH - width - marginRight,
            marginTop + height + buttonsMargin
        )
        button.text.apply {
            textSize = 36.0
            textFont = titleFont ?: DefaultUIFont
        }

        button.onUp {
            val modalDialog = controlsModal()
            modalDialog.addTo(baseView)

            modalDialogController.onShow()
        }

        return button
    }

    private fun controlsModal(): Container {
        val container = FixedSizeContainer(
            GameDimensions.V_WIDTH.toDouble(),
            GameDimensions.V_HEIGHT.toDouble()
        ).apply {
            solidRect(width, height, Colors.BLACK) {
                alpha = 0.75
            }

            val keysInfoTitle = keysInfoTitle()
            val keysInfoText = keysInfoText()
            val backButton = backButton()
        }

        return container
    }

    private fun Container.backButton(): CustomButton {
        val view = this

        val width = 258.6
        val height = 82.5

        return CustomButton(
            label = Localization.strings.scene.lobby.back,
            width = width,
            height = height,
            icon = buttonIconPurple?.slice(),
            activeIcon = buttonIconActive?.slice(),
            buttonId = -1
        ).apply {
            container.addTo(view)

            container.position(
                GameDimensions.V_WIDTH / 2.0 - width / 2.0,
                GameDimensions.V_HEIGHT - 64.0 - height
            )

            text.apply {
                textSize = 36.0
                textFont = titleFont ?: DefaultUIFont
            }

            container.onClick {
                view.removeFromParent()
                modalDialogController.onHide()
            }
        }
    }
}

typealias ModalDialogControllerCallback = () -> Unit

class ModalDialogController {

    private val onShowCallbacks = mutableMapOf<String, ModalDialogControllerCallback>()
    private val onHideCallbacks = mutableMapOf<String, ModalDialogControllerCallback>()

    fun registerOnShow(id: String, callback: ModalDialogControllerCallback) {
        onShowCallbacks[id] = callback
    }

    fun registerOnHide(id: String, callback: ModalDialogControllerCallback) {
        onHideCallbacks[id] = callback
    }

    fun onShow() {
        onShowCallbacks.values.forEach { it.invoke() }
    }

    fun onHide() {
        onHideCallbacks.values.forEach { it.invoke() }
    }

}
